import styled from 'styled-components';
import { Stack } from '@spotify-internal/encore-web/ads-business';
import { spacer4, Type } from '@spotify-internal/encore-web/core';
import { useT } from '@spotify-internal/i18n-core';
import { sendEvent } from '@spotify-internal/s4mp-page-analytics';
import { Patch } from '@hooks/useAlbumInfo/types';
import { CopyIconContainer } from '@components/Release/Deliveries/TableRowExpanded';
import { PillsContainer } from '@components/Release/TracksTab/styles';
import { CopyToGreenCheckAnimation } from '@components/shared/CopyToGreenCheckAnimation';
import { PillInfo } from '@components/shared/PillInfo/PillInfo';
import { PillInfoWithIcon } from '@components/shared/PillInfo/PillInfoWithIcon';
import { RowCell } from '@components/shared/styles';
import {
  AvailabilityClearanceStatus,
  FullRow,
} from '@components/Track/Availability/AvailabilityClearanceStatus';
import { EVENT_CATEGORY, EVENT_ACTION } from '@lib/ga/types';
import { EMPTY_VALUE, GA_PRODUCT_CATALOG } from '@helpers/constants';

const ShadowedStack = styled(Stack)`
  padding: 4px;
  box-shadow: 0px 5px 12px 0px #0000004d;
  margin: -12px -7px 5px -7px;
`;

const PaddedStack = styled(Stack)`
  padding: 28px;
  padding-bottom: 0;
`;

export const UnclearedVideosTableExpansionRow = ({
  patches,
  batchId,
  deliveryDate,
}: {
  patches: Patch[];
  batchId?: string;
  deliveryDate?: string;
}) => {
  const t = useT();
  const COPY_BATCH_ID = t(
    'atlas.actions.copy_batch_id',
    'Copy Batch ID',
    'Text for Copy Batch ID action',
  );
  const COPIED_TO_CLIPBOARD = t(
    'atlas.snackbars.copied_to_clipboard',
    'Copied to clipboard',
    'Snackbar message to indicate copied to clipboard',
  );
  return (
    <ShadowedStack gap={spacer4}>
      <PaddedStack>
        <PillsContainer>
          <FullRow>
            <RowCell marginRight>
              <PillInfoWithIcon
                label={t(
                  'atlas.labels.batch_id',
                  'Batch ID',
                  'Label for Batch ID',
                )}
                value={
                  <Type variant="viola" semanticColor="textSubdued">
                    {batchId || EMPTY_VALUE}
                  </Type>
                }
                icon={
                  <CopyIconContainer>
                    <CopyToGreenCheckAnimation
                      hasHoverBackground
                      label="Copy Batch ID"
                      tooltipText={COPY_BATCH_ID}
                      copyText={batchId || ''}
                      snackbarText={COPIED_TO_CLIPBOARD}
                      screenReaderText={COPY_BATCH_ID}
                      handleClick={() =>
                        sendEvent({
                          eventCategory:
                            EVENT_CATEGORY.RELEASE_DELIVERIES_SECTION,
                          eventAction: EVENT_ACTION.CLICK,
                          eventLabel: 'Batch ID copied',
                          product: GA_PRODUCT_CATALOG,
                        })
                      }
                    />
                  </CopyIconContainer>
                }
                backgroundColor="backgroundHighlight"
              />
            </RowCell>
            <RowCell marginLeft>
              <PillInfo
                label={t(
                  'atlas.labels.date_delivered',
                  'Date delivered (UTC)',
                  `Label for date delivered in UTC. Keep string '(UTC)' as is.`,
                )}
                value={deliveryDate || EMPTY_VALUE}
                tooltipMessage={undefined}
                backgroundColor="backgroundHighlight"
              />
            </RowCell>
          </FullRow>
        </PillsContainer>
      </PaddedStack>
      <AvailabilityClearanceStatus patches={patches} />
    </ShadowedStack>
  );
};
